/**
 * AnalyticsSettingsModal – user-level settings for the marketing analyst module.
 * Configurable: ad account selection (with search), per-account KPI targets,
 * conversion event mapping, data sources, and global KPI defaults.
 */
import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { IdealClientSheetSection } from "@/components/ideal-client-sheet-section";
import { useToast } from "@/hooks/use-toast";
import { useDashboardCurrency } from "@/hooks/useDashboardCurrency";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Field, FieldLabel } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  BarChart3,
  Target,
  DollarSign,
  Users,
  Phone,
  TrendingUp,
  Check,
  Search,
  X,
  ChevronDown,
  ChevronUp,
  Zap,
  MessageSquare,
  Send,
  ExternalLink,
  FileSpreadsheet,
  Plug,
  ShoppingBag,
  ShoppingCart,
  Eye,
  Layers,
  Activity,
  Calendar,
  Monitor,
  Music2,
} from "lucide-react";
import type { QuarterlyTargets, QuarterlyTarget, MonthlyTargets } from "@shared/schema";
import { SlackNotificationsPanel } from "@/components/slack-notifications-panel";
import { useAuth } from "@/hooks/useAuth";
import { canAccessGoogleAdsIntegration } from "@shared/google-ads-access";
import { BlockSkeleton } from "@/components/skeletons";

interface AdAccount {
  id: string;
  name: string;
  accountStatus: number;
  currency: string;
}

interface MetaStatus {
  connected: boolean;
  tokenExpired?: boolean;
  metaUserName?: string;
  adAccounts?: AdAccount[];
  selectedAdAccountId?: string;
}

interface AccountConfig {
  kpiCostPerLead?: string | null;
  kpiCostPerCall?: string | null;
  kpiCostPerClient?: string | null;
  kpiTargetRoas?: string | null;
  // Ecommerce-specific KPI targets
  kpiTargetCpa?: string | null;
  kpiTargetCpm?: string | null;
  kpiTargetAov?: string | null;
  // SaaS-specific KPI targets
  kpiCostPerTrial?: string | null;
  kpiCostPerSubscription?: string | null;
  conversionLead?: string | null;
  conversionCall?: string | null;
  conversionSale?: string | null;
  conversionTrial?: string | null;
  conversionSubscription?: string | null;
}

interface AnalyticsSettingsData {
  selectedAdAccountIds: string[];
  dataSources: { meta: boolean; ghl: boolean; googleSheets: boolean; hubspot: boolean };
  kpiCostPerLead: string | null;
  kpiCostPerCall: string | null;
  kpiCostPerClient: string | null;
  kpiTargetRoas: string | null;
  accountConfigs: Record<string, AccountConfig>;
  googleSheetUrl: string | null;
  googleSheetColumnMapping: Record<string, string> | null;
  dashboardMode?: string;
  setterCalendarIds?: string[];
  setterCallSource?: string;
  // Pola zapisywane przez ten modal — sa w tabeli analytics_settings, brakowalo ich w typie.
  allegroMonitorEnabled?: boolean;
  monitoringEnabled?: boolean;
  quarterlyTargets?: QuarterlyTargets | null;
  monthlyTargets?: MonthlyTargets | null;
  ghlAttributionSource?: "ghl" | "tracking_events";
  ecommerceSource?: string;
}

interface AnalyticsSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  forClientUserId?: string;
}

// Common Meta conversion action types for autocomplete hints
const COMMON_CONVERSIONS = [
  "lead",
  "offsite_conversion.fb_pixel_lead",
  "offsite_conversion.fb_pixel_custom",
  "onsite_conversion.lead_grouped",
  "onsite_conversion.messaging_first_reply",
  "schedule",
  "offsite_conversion.fb_pixel_schedule",
  "purchase",
  "offsite_conversion.fb_pixel_purchase",
  "complete_registration",
  "offsite_conversion.fb_pixel_complete_registration",
  "contact",
  "submit_application",
  "other",
];

// Auto-suggest column mapping based on header names
const SUGGEST_MAP: Record<string, string> = {
  "źródło kontaktu": "source",
  "zrodlo kontaktu": "source",
  źródło: "source",
  source: "source",
  "imię i nazwisko": "name",
  "imie i nazwisko": "name",
  nazwa: "name",
  name: "name",
  email: "email",
  "e-mail": "email",
  mail: "email",
  telefon: "phone",
  tel: "phone",
  phone: "phone",
  "numer telefonu": "phone",
  data: "date",
  date: "date",
  "data kontaktu": "date",
  "data leada": "date",
  budżet: "budget",
  budzet: "budget",
  budget: "budget",
  "kiedy chce kupić": "buyTimeline",
  "kiedy chce kupic": "buyTimeline",
  timeline: "buyTimeline",
  cel: "goal",
  goal: "goal",
  "co się podoba": "interest",
  "co sie podoba": "interest",
  zainteresowanie: "interest",
  status: "status",
  "status leada": "status",
  kwalifikacja: "qualification",
  jakość: "qualification",
  jakosc: "qualification",
  wynik: "result",
  "wynik rozmowy": "result",
  rezultat: "result",
  "campaign id": "campaignId",
  campaign_id: "campaignId",
  "kampania id": "campaignId",
  "adset id": "adsetId",
  adset_id: "adsetId",
  "zestaw reklam id": "adsetId",
  "ad id": "adId",
  ad_id: "adId",
  "reklama id": "adId",
};

function autoSuggestMapping(headers: string[]): Record<string, string> {
  const mapping: Record<string, string> = {};
  for (let i = 0; i < headers.length; i++) {
    const lower = headers[i].toLowerCase().trim();
    if (SUGGEST_MAP[lower]) {
      mapping[String(i)] = SUGGEST_MAP[lower];
    } else if (/^notatki\s*(handlowca)?\s*\d*$/i.test(lower)) {
      mapping[String(i)] = "notes";
    }
  }
  return mapping;
}

// ─── Integration Row ──────────────────────────────────────
function IntegrationRow({
  name,
  connected,
  tokenExpired,
  detail,
  color,
  logo,
  onConnect,
  onDisconnect,
}: {
  name: string;
  connected: boolean;
  tokenExpired?: boolean;
  detail?: string;
  color: string;
  logo: React.ReactNode;
  onConnect: () => void;
  onDisconnect: () => void;
}) {
  const { t } = useTranslation("analyticsSettings");
  const [loading, setLoading] = useState(false);
  const handleAction = async (action: () => void | Promise<void>) => {
    setLoading(true);
    try {
      await action();
    } catch (e) {
      // eslint-disable-next-line no-console -- jedyny slad bledu; apps/web nie ma loggera
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-between bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <div
          className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: color }}
        >
          {logo}
        </div>
        <div className="min-w-0">
          <span className="text-sm text-white">{name}</span>
          {connected && detail && <p className="text-[10px] text-white/40 truncate">{detail}</p>}
          {tokenExpired && (
            <p className="text-[10px] text-amber-400 truncate">
              {t("integrationRow.tokenExpired")}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {tokenExpired ? (
          <Button
            size="sm"
            onClick={() => handleAction(onConnect)}
            disabled={loading}
            className="text-[10px] h-6 px-2.5 text-white bg-amber-600 hover:bg-amber-500"
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : t("integrationRow.reconnect")}
          </Button>
        ) : connected ? (
          <>
            <Badge className="text-[10px] bg-green-600/20 text-green-400 border-green-500/50">
              {t("integrationRow.connected")}
            </Badge>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleAction(onDisconnect)}
              disabled={loading}
              className="text-[10px] text-red-400 hover:text-red-300 hover:bg-red-500/10 h-6 px-1.5"
            >
              {loading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                t("integrationRow.disconnect")
              )}
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            onClick={() => handleAction(onConnect)}
            disabled={loading}
            className="text-[10px] h-6 px-2.5 text-white"
            style={{ backgroundColor: color }}
          >
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : t("integrationRow.connect")}
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Google Ads manual Customer ID input ──────────────────
function GoogleAdsCustomerIdInline({
  onSave,
}: {
  onSave: (id: string, name: string) => Promise<void>;
}) {
  const { t } = useTranslation("analyticsSettings");
  const [customerId, setCustomerId] = useState("");
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<{
    valid?: boolean;
    descriptiveName?: string;
    currencyCode?: string;
    error?: string;
  } | null>(null);

  const handleValidate = async () => {
    const cleaned = customerId.replace(/[-\s]/g, "");
    if (cleaned.length < 7) return;
    setValidating(true);
    setValidation(null);
    try {
      const result = (await apiRequest("POST", "/api/integrations/google-ads/validate-customer", {
        customerId: cleaned,
      })) as { valid: boolean; descriptiveName?: string; currencyCode?: string; error?: string };
      setValidation(result);
    } catch {
      setValidation({ valid: false, error: t("googleAdsInline.serverError") });
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    const cleaned = customerId.replace(/[-\s]/g, "");
    if (!validation?.valid) return;
    setSaving(true);
    try {
      await onSave(
        cleaned,
        validation.descriptiveName || t("googleAdsInline.accountFallback", { id: cleaned })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ml-8 -mt-1 space-y-1.5 p-2 bg-white/[0.03] rounded border border-white/[0.08]">
      <p className="text-[10px] text-white/40">{t("googleAdsInline.hint")}</p>
      <div className="flex items-center gap-1.5">
        <Input
          placeholder={t("googleAdsInline.placeholder")}
          value={customerId}
          onChange={(e) => {
            setCustomerId(e.target.value);
            setValidation(null);
          }}
          onKeyDown={(e) => e.key === "Enter" && handleValidate()}
          className="h-7 text-[11px] bg-[#030407] border-white/[0.1] text-white flex-1"
        />
        {!validation?.valid ? (
          <Button
            size="sm"
            onClick={handleValidate}
            disabled={validating || customerId.replace(/[-\s]/g, "").length < 7}
            className="h-7 text-[10px] px-2 bg-[#4285F4] hover:bg-[#3b78e7] text-white"
          >
            {validating ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              t("googleAdsInline.validate")
            )}
          </Button>
        ) : (
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="h-7 text-[10px] px-2 bg-green-600 hover:bg-green-700 text-white"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : t("googleAdsInline.connect")}
          </Button>
        )}
      </div>
      {validation && (
        <div
          className={`flex items-center gap-1.5 text-[10px] ${validation.valid ? "text-green-400" : "text-red-400"}`}
        >
          <span>
            {validation.valid
              ? `✓ ${validation.descriptiveName} (${validation.currencyCode})`
              : `✕ ${validation.error}`}
          </span>
        </div>
      )}
    </div>
  );
}

function GoogleAdsAccountMultiSelectInline({
  accounts,
  selectedCustomerId,
  onSelectionChange,
  onRefresh,
}: {
  accounts: { id: string; descriptiveName: string; currencyCode: string }[];
  selectedCustomerId: string;
  onSelectionChange: (ids: string[]) => Promise<void>;
  onRefresh: () => Promise<void>;
}) {
  const { t } = useTranslation("analyticsSettings");
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [localIds, setLocalIds] = useState<string[]>(() =>
    selectedCustomerId ? selectedCustomerId.split(",").filter(Boolean) : []
  );
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalIds(selectedCustomerId ? selectedCustomerId.split(",").filter(Boolean) : []);
  }, [selectedCustomerId]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = (id: string) => {
    setLocalIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      onSelectionChange(next);
      return next;
    });
  };

  const selectedIds = localIds;

  const selectedCount = selectedIds.length;
  const label =
    selectedCount === 0
      ? t("googleAdsMultiSelect.placeholder")
      : selectedCount === 1
        ? accounts.find((a) => a.id === selectedIds[0])?.descriptiveName || selectedIds[0]
        : t("googleAdsMultiSelect.selectedMany", { count: selectedCount });

  return (
    <div className="ml-8 -mt-1 space-y-1">
      <div className="flex items-center gap-2" ref={ref}>
        <div className="relative flex-1">
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="h-7 w-full flex items-center justify-between px-2 text-[11px] bg-[#030407] border border-white/[0.1] text-white rounded hover:border-white/[0.2] transition-colors"
          >
            <span className="truncate">{label}</span>
            <ChevronDown
              className={`h-3 w-3 ml-1 text-white/40 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </button>
          {open && (
            <div className="absolute z-50 mt-1 w-full bg-[#0a0c10] border border-white/[0.12] rounded-lg shadow-xl overflow-hidden">
              <div className="max-h-48 overflow-y-auto">
                {accounts.map((a) => {
                  const isSelected = selectedIds.includes(a.id);
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggle(a.id)}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-left hover:bg-white/[0.05] transition-colors ${isSelected ? "bg-white/[0.03]" : ""}`}
                    >
                      <div
                        className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-[#4285F4] border-[#4285F4]" : "border-white/20"}`}
                      >
                        {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <span className="text-white truncate">{a.descriptiveName}</span>
                      <span className="text-white/40 ml-auto flex-shrink-0">
                        ({a.id}) – {a.currencyCode}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={async () => {
            setRefreshing(true);
            await onRefresh().finally(() => setRefreshing(false));
          }}
          disabled={refreshing}
          className="h-7 px-1.5 text-[10px] text-white/50 hover:text-white/70"
          title={t("integrations.refreshTitle")}
        >
          {refreshing ? "⏳" : "↻"}
        </Button>
      </div>
    </div>
  );
}

export default function AnalyticsSettingsModal({
  open,
  onOpenChange,
  forClientUserId,
}: AnalyticsSettingsModalProps) {
  const { t } = useTranslation("analyticsSettings");
  const sheetFieldOptions = [
    { value: "source", label: t("sheetFields.source") },
    { value: "name", label: t("sheetFields.name") },
    { value: "email", label: t("sheetFields.email") },
    { value: "phone", label: t("sheetFields.phone") },
    { value: "date", label: t("sheetFields.date") },
    { value: "budget", label: t("sheetFields.budget") },
    { value: "buyTimeline", label: t("sheetFields.buyTimeline") },
    { value: "goal", label: t("sheetFields.goal") },
    { value: "interest", label: t("sheetFields.interest") },
    { value: "status", label: t("sheetFields.status") },
    { value: "qualification", label: t("sheetFields.qualification") },
    { value: "result", label: t("sheetFields.result") },
    { value: "campaignId", label: t("sheetFields.campaignId") },
    { value: "adsetId", label: t("sheetFields.adsetId") },
    { value: "adId", label: t("sheetFields.adId") },
    { value: "notes", label: t("sheetFields.notes") },
  ];
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const dc = useDashboardCurrency();
  const currLabel = dc.currency;
  const currSymbol = dc.symbol;

  const { user: authUser } = useAuth();

  const settingsUrl = forClientUserId
    ? `/api/analytics-settings?forClientUserId=${forClientUserId}`
    : "/api/analytics-settings";

  // Fetch current settings
  const { data: settings, isLoading: settingsLoading } = useQuery<AnalyticsSettingsData>({
    queryKey: ["analytics-settings", forClientUserId ?? ""],
    queryFn: () => apiRequest("GET", settingsUrl),
    enabled: open,
  });

  // Build integration status URL with optional forUserId param
  const integrationStatusUrl = (base: string) =>
    forClientUserId ? `${base}?forUserId=${encodeURIComponent(forClientUserId)}` : base;

  // Fetch Meta connection status (for ad account list)
  const { data: metaStatus } = useQuery<MetaStatus>({
    queryKey: ["meta-integration-status", forClientUserId ?? ""],
    queryFn: () => apiRequest("GET", integrationStatusUrl("/api/integrations/meta/status")),
    enabled: open,
    staleTime: 30_000,
  });

  // VP-192: Google Ads zniknelo z UI (aplikacja nie przeszla weryfikacji Google
  // OAuth — podlaczenie konczy sie 403 access_denied). Status pobieramy tylko dla
  // super_admina, ktory jako jedyny widzi te integracje.
  const canSeeGoogleAds = canAccessGoogleAdsIntegration(authUser);

  // Integration statuses
  const { data: googleAdsStatus } = useQuery<{
    connected: boolean;
    googleEmail?: string;
    selectedCustomerId?: string;
    selectedCustomerName?: string;
    customerAccounts?: { id: string; descriptiveName: string; currencyCode: string }[];
  }>({
    queryKey: ["google-ads-integration-status", forClientUserId ?? ""],
    queryFn: () => apiRequest("GET", integrationStatusUrl("/api/integrations/google-ads/status")),
    enabled: open && canSeeGoogleAds,
    staleTime: 30_000,
  });
  const { data: ghlStatus } = useQuery<{ connected: boolean; locationName?: string }>({
    queryKey: ["ghl-integration-status", forClientUserId ?? ""],
    queryFn: () => apiRequest("GET", integrationStatusUrl("/api/integrations/crm/status")),
    enabled: open,
    staleTime: 30_000,
  });
  const { data: allegroStatus } = useQuery<{ connected: boolean; allegroLogin?: string }>({
    queryKey: ["allegro-integration-status", forClientUserId ?? ""],
    queryFn: () => apiRequest("GET", integrationStatusUrl("/api/integrations/allegro/status")),
    enabled: open,
    staleTime: 30_000,
  });
  const { data: shopifyStatus } = useQuery<{ connected: boolean; shopDomain?: string }>({
    queryKey: ["shopify-integration-status", forClientUserId ?? ""],
    queryFn: () => apiRequest("GET", integrationStatusUrl("/api/integrations/shopify/status")),
    enabled: open,
    staleTime: 30_000,
  });
  const { data: baselinkerStatus } = useQuery<{ connected: boolean }>({
    queryKey: ["baselinker-integration-status", forClientUserId ?? ""],
    queryFn: () => apiRequest("GET", integrationStatusUrl("/api/integrations/baselinker/status")),
    enabled: open,
    staleTime: 30_000,
  });
  const {
    data: ringostatStatus,
    isError: ringostatError,
    refetch: refetchRingostat,
  } = useQuery<{
    connected: boolean;
    projectId?: string;
    projectName?: string;
    webhookUrl?: string;
  }>({
    queryKey: ["ringostat-integration-status", forClientUserId ?? ""],
    queryFn: () => apiRequest("GET", integrationStatusUrl("/api/integrations/ringostat/status")),
    enabled: open,
    staleTime: 30_000,
  });

  const { data: gmcStatus } = useQuery<{ connected: boolean; googleEmail?: string }>({
    queryKey: ["gmc-integration-status", forClientUserId ?? ""],
    queryFn: () =>
      apiRequest("GET", integrationStatusUrl("/api/integrations/google-merchant/status")),
    enabled: open,
    staleTime: 30_000,
  });

  const { data: hubspotStatus } = useQuery<{ connected: boolean; portalName?: string }>({
    queryKey: ["hubspot-integration-status", forClientUserId ?? ""],
    queryFn: () => apiRequest("GET", integrationStatusUrl("/api/integrations/hubspot/status")),
    enabled: open,
    staleTime: 30_000,
  });

  const { data: tiktokStatus, refetch: refetchTiktok } = useQuery<{
    connected: boolean;
    tiktokUsername?: string;
  }>({
    queryKey: ["tiktok-integration-status", forClientUserId ?? ""],
    queryFn: () => apiRequest("GET", "/api/integrations/tiktok/status"),
    enabled: open,
    staleTime: 30_000,
  });

  // Local state
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [dataSources, setDataSources] = useState<{
    meta: boolean;
    ghl: boolean;
    googleSheets: boolean;
    hubspot: boolean;
  }>({ meta: true, ghl: false, googleSheets: false, hubspot: false });
  const [kpiCostPerLead, setKpiCostPerLead] = useState("");
  const [kpiCostPerCall, setKpiCostPerCall] = useState("");
  const [kpiCostPerClient, setKpiCostPerClient] = useState("");
  const [kpiTargetRoas, setKpiTargetRoas] = useState("");
  const [accountConfigs, setAccountConfigs] = useState<Record<string, AccountConfig>>({});
  const [allegroMonitorEnabled, setAllegroMonitorEnabled] = useState(false);
  const [monitoringEnabled, setMonitoringEnabled] = useState(false);
  const [quarterlyTargets, setQuarterlyTargets] = useState<QuarterlyTargets>({});
  const [monthlyTargets, setMonthlyTargets] = useState<MonthlyTargets>({});
  const [testingMonitor, setTestingMonitor] = useState(false);
  // Google Sheets state
  const [googleSheetUrl, setGoogleSheetUrl] = useState("");
  const [googleSheetColumnMapping, setGoogleSheetColumnMapping] = useState<Record<string, string>>(
    {}
  );
  const [sheetHeaders, setSheetHeaders] = useState<string[]>([]);
  const [fetchingHeaders, setFetchingHeaders] = useState(false);
  const [sheetTestResult, setSheetTestResult] = useState<string | null>(null);

  // UI state
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedAccount, setExpandedAccount] = useState<string | null>(null);
  // Shopify / BaseLinker inline connect state
  const [shopifyDomain, setShopifyDomain] = useState("");
  const [baselinkerToken, setBaselinkerToken] = useState("");
  const [expandedIntegration, setExpandedIntegration] = useState<string | null>(null);
  // Dashboard mode & setter calendar state
  const [dashboardMode, setDashboardMode] = useState<string>("lead_gen_basic");
  const [setterCalendarIds, setSetterCalendarIds] = useState<string[]>([]);
  const [setterCallSource, setSetterCallSource] = useState<string>("ghl");
  const [ghlAttributionSource, setGhlAttributionSource] = useState<"ghl" | "tracking_events">(
    "ghl"
  );
  // Ecommerce data source state
  const [ecommerceSource, setEcommerceSource] = useState<string>("meta");

  // Conversion verification state
  const [verifyingConversion, setVerifyingConversion] = useState<string | null>(null); // "accId:field"
  const [conversionVerifyResult, setConversionVerifyResult] = useState<
    Record<
      string,
      { found: boolean; count: number; allConversions: { type: string; count: number }[] } | null
    >
  >({});

  const verifyConversion = async (accountId: string, field: string, conversionType: string) => {
    if (!conversionType) return;
    const key = `${accountId}:${field}`;
    setVerifyingConversion(key);
    setConversionVerifyResult((prev) => ({ ...prev, [key]: null }));
    try {
      const result = (await apiRequest("POST", "/api/analytics-settings/conversion/verify", {
        adAccountId: accountId,
        conversionType,
        forClientUserId,
      })) as {
        ok: boolean;
        found: boolean;
        count: number;
        allConversions: { type: string; count: number }[];
      };
      setConversionVerifyResult((prev) => ({
        ...prev,
        [key]: {
          found: result.found,
          count: result.count,
          allConversions: result.allConversions || [],
        },
      }));
    } catch (err) {
      const errorMsg = (err as Error)?.message?.includes(":")
        ? (err as Error).message.split(":").slice(1).join(":").trim()
        : t("accountSettings.verifyErrorDesc");
      // Try to parse JSON error message from server
      let displayMsg = errorMsg;
      try {
        const parsed = JSON.parse(errorMsg);
        displayMsg = parsed.message || errorMsg;
      } catch {
        /* not JSON */
      }
      toast({
        title: t("accountSettings.verifyErrorTitle"),
        description: displayMsg,
        variant: "destructive",
      });
    } finally {
      setVerifyingConversion(null);
    }
  };

  // Sync server state → local state
  useEffect(() => {
    if (settings) {
      setSelectedAccounts(settings.selectedAdAccountIds || []);
      setDataSources(
        settings.dataSources || { meta: true, ghl: false, googleSheets: false, hubspot: false }
      );
      setKpiCostPerLead(settings.kpiCostPerLead || "");
      setKpiCostPerCall(settings.kpiCostPerCall || "");
      setKpiCostPerClient(settings.kpiCostPerClient || "");
      setKpiTargetRoas(settings.kpiTargetRoas || "");
      setAccountConfigs(settings.accountConfigs || {});
      setAllegroMonitorEnabled(settings.allegroMonitorEnabled ?? false);
      setMonitoringEnabled(settings.monitoringEnabled ?? false);
      setQuarterlyTargets(settings.quarterlyTargets || {});
      setMonthlyTargets(settings.monthlyTargets || {});
      setGoogleSheetUrl(settings.googleSheetUrl || "");
      setGoogleSheetColumnMapping(settings.googleSheetColumnMapping || {});
      setDashboardMode(settings.dashboardMode || "lead_gen_basic");
      setSetterCalendarIds(settings.setterCalendarIds || []);
      setSetterCallSource(settings.setterCallSource || "ghl");
      setGhlAttributionSource(settings.ghlAttributionSource || "ghl");
      setEcommerceSource(settings.ecommerceSource || "meta");
    }
  }, [settings]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: Partial<AnalyticsSettingsData>) =>
      apiRequest(
        "PUT",
        "/api/analytics-settings",
        forClientUserId ? { ...data, forClientUserId } : data
      ),
    onSuccess: () => {
      onOpenChange(false);
      queryClient.invalidateQueries({ queryKey: ["analytics-settings", forClientUserId ?? ""] });
      queryClient.invalidateQueries({
        queryKey: ["/api/analytics-settings", forClientUserId ?? ""],
      });
      toast({ title: t("savedTitle"), description: t("savedDesc") });
    },
    onError: () => {
      toast({ title: t("errorTitle"), description: t("errorDesc"), variant: "destructive" });
    },
  });

  const handleSave = () => {
    saveMutation.mutate({
      selectedAdAccountIds: selectedAccounts,
      dataSources,
      kpiCostPerLead: kpiCostPerLead || null,
      kpiCostPerCall: kpiCostPerCall || null,
      kpiCostPerClient: kpiCostPerClient || null,
      kpiTargetRoas: kpiTargetRoas || null,
      accountConfigs,
      allegroMonitorEnabled,
      monitoringEnabled,
      googleSheetUrl: googleSheetUrl || null,
      googleSheetColumnMapping:
        Object.keys(googleSheetColumnMapping).length > 0 ? googleSheetColumnMapping : null,
      dashboardMode,
      setterCalendarIds,
      setterCallSource,
      ghlAttributionSource,
      ecommerceSource,
      quarterlyTargets: Object.keys(quarterlyTargets).length > 0 ? quarterlyTargets : null,
      monthlyTargets: Object.keys(monthlyTargets).length > 0 ? monthlyTargets : null,
    });
  };

  const toggleAccount = (accountId: string) => {
    setSelectedAccounts((prev) =>
      prev.includes(accountId) ? prev.filter((id) => id !== accountId) : [...prev, accountId]
    );
  };

  const updateAccountConfig = (accountId: string, field: keyof AccountConfig, value: string) => {
    setAccountConfigs((prev) => ({
      ...prev,
      [accountId]: { ...prev[accountId], [field]: value || null },
    }));
  };

  // useMemo: nowa referencja tablicy przy każdym renderze przebijała memoizację
  // niżej w komponencie (react-hooks/exhaustive-deps).
  const adAccounts = useMemo(() => metaStatus?.adAccounts || [], [metaStatus]);

  // Helper: open OAuth popup and invalidate queries on close
  const openOAuthPopup = (url: string, windowName: string, queryKeysToInvalidate: string[]) => {
    const w = 600,
      h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(
      url,
      windowName,
      `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
    );
    if (!popup) {
      // Popup blocked — fallback to same-tab redirect
      window.location.href = url;
      return;
    }
    const check = setInterval(() => {
      if (popup?.closed) {
        clearInterval(check);
        queryKeysToInvalidate.forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
      }
    }, 500);
  };

  // Helper: start OAuth flow — opens popup immediately to avoid browser blocking, then redirects to OAuth URL
  const startOAuthFlow = async (
    apiEndpoint: string,
    windowName: string,
    queryKeysToInvalidate: string[]
  ) => {
    const w = 600,
      h = 700;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    // Open popup IMMEDIATELY on click (before any async) to avoid browser popup blocker
    const popup = window.open(
      "about:blank",
      windowName,
      `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`
    );
    try {
      const data = await apiRequest<{ url?: string }>("GET", apiEndpoint);
      if (!data.url) {
        popup?.close();
        return;
      }
      if (popup && !popup.closed) {
        popup.location.href = data.url;
      } else {
        // Popup was blocked or closed — fallback
        window.location.href = data.url;
      }
      if (popup) {
        const check = setInterval(() => {
          if (popup?.closed) {
            clearInterval(check);
            queryKeysToInvalidate.forEach((key) =>
              queryClient.invalidateQueries({ queryKey: [key] })
            );
          }
        }, 500);
      }
    } catch (e) {
      popup?.close();
      throw e;
    }
  };

  // Search + filter: only show max 5 non-selected results
  const filteredAccounts = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return adAccounts
      .filter((a) => !selectedAccounts.includes(a.id))
      .filter((a) => a.name.toLowerCase().includes(q) || a.id.toLowerCase().includes(q))
      .slice(0, 5);
  }, [adAccounts, searchQuery, selectedAccounts]);

  // Selected accounts with their meta info
  const selectedAccountsMeta = useMemo(
    () => adAccounts.filter((a) => selectedAccounts.includes(a.id)),
    [adAccounts, selectedAccounts]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] bg-[#030407] border-white/[0.1] text-white max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 bg-red-600/20 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-4 h-4 text-red-400" />
            </div>
            {t("title")}
          </DialogTitle>
        </DialogHeader>

        {settingsLoading ? (
          // VP-208: placeholder sekcji ustawień zamiast spinnera — modal ma stałą
          // wysokość, więc treść nie przeskakuje po dociągnięciu konfiguracji.
          <div className="space-y-5 py-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <BlockSkeleton className="h-4 w-40" />
                <BlockSkeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-5 py-2 overflow-y-auto pr-1 flex-1 min-h-0">
            {/* ═══ Dashboard Mode ═══ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-[#ef3e29]" />
                {/* Dashboard Mode */}
                <h3 className="text-sm font-semibold text-white">{t("dashboardMode.title")}</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { value: "ecommerce", label: "Ecommerce", icon: ShoppingCart },
                  { value: "lead_gen_basic", label: "Lead Gen", icon: Users },
                  { value: "lead_gen_advanced", label: "Lead Gen+", icon: Phone },
                  { value: "saas", label: "SaaS", icon: Monitor },
                  { value: "autowebinar_book_a_call", label: "Autowebinar + Call", icon: Calendar },
                  { value: "vsl_funnel", label: "VSL Funnel", icon: Activity },
                ].map((mode) => (
                  <button
                    key={mode.value}
                    onClick={() => setDashboardMode(mode.value)}
                    className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-lg border text-xs font-medium transition-all ${
                      dashboardMode === mode.value
                        ? "bg-[#ef3e29]/15 border-[#ef3e29]/50 text-white"
                        : "bg-white/[0.04] border-white/[0.1] text-white/50 hover:border-white/[0.2] hover:text-white/70"
                    }`}
                  >
                    <mode.icon className="w-4 h-4" />
                    {mode.label}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-white/30">
                {dashboardMode === "ecommerce" && t("dashboardMode.ecommerceDesc")}
                {dashboardMode === "lead_gen_basic" && t("dashboardMode.leadGenBasicDesc")}
                {dashboardMode === "lead_gen_advanced" && t("dashboardMode.leadGenAdvancedDesc")}
                {dashboardMode === "saas" && t("dashboardMode.saasDesc")}
                {dashboardMode === "autowebinar_book_a_call" && t("dashboardMode.autowebinarDesc")}
                {dashboardMode === "vsl_funnel" && t("dashboardMode.vslDesc")}
              </p>
            </div>

            {/* ═══ Ecommerce Data Source Selector (ecommerce only) ═══ */}
            {dashboardMode === "ecommerce" && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4 text-[#ef3e29]" />
                  <h3 className="text-sm font-semibold text-white">{t("ecommerceSource.title")}</h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    {
                      value: "meta",
                      label: "Meta Ads",
                      connected: metaStatus?.connected,
                      desc: metaStatus?.connected
                        ? t("ecommerceSource.connected")
                        : t("ecommerceSource.notConnected"),
                      color: "#1877F2",
                      icon: (
                        <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                        </svg>
                      ),
                    },
                    {
                      value: "allegro",
                      label: "Allegro",
                      connected: allegroStatus?.connected,
                      desc: allegroStatus?.connected
                        ? `✓ ${allegroStatus.allegroLogin || t("ecommerceSource.connectedSimple")}`
                        : t("ecommerceSource.notConnected"),
                      color: "#FF5A00",
                      icon: <span className="text-white font-bold text-xs">A</span>,
                    },
                  ].map((src) => (
                    <button
                      key={src.value}
                      onClick={() => setEcommerceSource(src.value)}
                      className={`flex flex-col items-start gap-1 px-3 py-3 rounded-lg border text-xs font-medium transition-all ${
                        ecommerceSource === src.value
                          ? "bg-[#ef3e29]/15 border-[#ef3e29]/50 text-white"
                          : "bg-white/[0.04] border-white/[0.1] text-white/50 hover:border-white/[0.2] hover:text-white/70"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-5 h-5 rounded flex items-center justify-center"
                          style={{ backgroundColor: src.color }}
                        >
                          {src.icon}
                        </div>
                        <span className="font-semibold">{src.label}</span>
                      </div>
                      <span
                        className={`text-[10px] ${src.connected ? "text-emerald-400" : "text-white/30"}`}
                      >
                        {src.desc}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-white/30">{t("ecommerceSource.desc")}</p>
              </div>
            )}

            {/* ═══ Setter Call Source Selector (lead_gen_advanced / funnel modes) ═══ */}
            {(dashboardMode === "lead_gen_advanced" ||
              dashboardMode === "autowebinar_book_a_call" ||
              dashboardMode === "vsl_funnel") && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#ef3e29]" />
                  <h3 className="text-sm font-semibold text-white">{t("callSource.title")}</h3>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    {
                      value: "ghl",
                      label: "LeadConnector",
                      connected: ghlStatus?.connected,
                      desc: ghlStatus?.connected
                        ? t("callSource.connected")
                        : t("callSource.notConnected"),
                    },
                    {
                      value: "ringostat",
                      label: "Ringostat",
                      connected: ringostatStatus?.connected,
                      desc: ringostatStatus?.connected
                        ? `✓ ${ringostatStatus.projectName || t("callSource.connectedSimple")}`
                        : t("callSource.notConnected"),
                    },
                  ].map((src) => (
                    <button
                      key={src.value}
                      onClick={() => setSetterCallSource(src.value)}
                      className={`flex flex-col items-start gap-1 px-3 py-3 rounded-lg border text-xs font-medium transition-all ${
                        setterCallSource === src.value
                          ? "bg-[#ef3e29]/15 border-[#ef3e29]/50 text-white"
                          : "bg-white/[0.04] border-white/[0.1] text-white/50 hover:border-white/[0.2] hover:text-white/70"
                      }`}
                    >
                      <span className="font-semibold">{src.label}</span>
                      <span
                        className={`text-[10px] ${src.connected ? "text-emerald-400" : "text-white/30"}`}
                      >
                        {src.desc}
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-white/30">{t("callSource.desc")}</p>
              </div>
            )}

            {/* Ringostat & Setter Calendar removed — connect/configure them
                inside Centrum Integracji to keep this modal focused on
                dashboard-only settings. */}

            {/* ═══ Section 1: Ad Account Selection ═══ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-blue-400" />
                <h3 className="text-sm font-semibold text-white">{t("adAccounts.title")}</h3>
                {selectedAccounts.length > 0 && (
                  <Badge className="text-[10px] bg-red-600/20 text-red-400 border-red-500/50">
                    {t("adAccounts.selected", { count: selectedAccounts.length })}
                  </Badge>
                )}
              </div>

              {!metaStatus?.connected ? (
                <div className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-3">
                  <p className="text-xs text-white/40">{t("adAccounts.notConnected")}</p>
                </div>
              ) : (
                <>
                  {/* Selected accounts as removable chips */}
                  {selectedAccountsMeta.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedAccountsMeta.map((acc) => (
                        <button
                          key={acc.id}
                          onClick={() => toggleAccount(acc.id)}
                          className="flex items-center gap-1 px-2 py-1 rounded-md bg-red-500/15 border border-red-500/30 text-xs text-red-300 hover:bg-red-500/25 transition-colors group"
                        >
                          <span className="truncate max-w-[140px]">{acc.name}</span>
                          <X className="w-3 h-3 text-red-400 group-hover:text-white flex-shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Search field */}
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/40" />
                    <Input
                      placeholder={t("adAccounts.searchPlaceholder")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-8 pl-8 text-xs bg-[#0a0c10] border-white/[0.1] text-white placeholder:text-white/40"
                    />
                  </div>

                  {/* Search results (max 5) */}
                  {filteredAccounts.length > 0 && (
                    <div className="space-y-1 max-h-[180px] overflow-y-auto">
                      {filteredAccounts.map((acc) => (
                        <button
                          key={acc.id}
                          onClick={() => {
                            toggleAccount(acc.id);
                            setSearchQuery("");
                          }}
                          className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] hover:bg-[#0a0c10] hover:border-red-500/30 transition-colors text-left text-xs"
                        >
                          <div className="w-4 h-4 rounded bg-white/[0.08] flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-white font-medium truncate">{acc.name}</p>
                            <p className="text-white/40 text-[10px]">
                              {acc.id} · {acc.currency}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Empty search result */}
                  {searchQuery.trim() && filteredAccounts.length === 0 && (
                    <p className="text-xs text-white/40 text-center py-2">
                      {t("adAccounts.noResults", { query: searchQuery })}
                    </p>
                  )}

                  {/* Hint when no search */}
                  {!searchQuery.trim() &&
                    selectedAccounts.length === 0 &&
                    adAccounts.length > 0 && (
                      <p className="text-xs text-white/40">{t("adAccounts.hint")}</p>
                    )}
                </>
              )}
            </div>

            {/* ═══ Section 2: Per-Account Settings ═══ */}
            {selectedAccountsMeta.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-400" />
                  <h3 className="text-sm font-semibold text-white">{t("accountSettings.title")}</h3>
                </div>
                <p className="text-xs text-white/60">{t("accountSettings.desc")}</p>

                <div className="space-y-1.5">
                  {selectedAccountsMeta.map((acc) => {
                    const cfg = accountConfigs[acc.id] || {};
                    const isExpanded = expandedAccount === acc.id;
                    const hasConfig = Object.values(cfg).some((v) => v);

                    return (
                      <div
                        key={acc.id}
                        className="rounded-lg border border-white/[0.08] overflow-hidden"
                      >
                        {/* Account header */}
                        <button
                          onClick={() => setExpandedAccount(isExpanded ? null : acc.id)}
                          className="w-full flex items-center justify-between px-3 py-2.5 bg-white/[0.04] hover:bg-[#0a0c10] transition-colors text-left"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-5 h-5 rounded bg-red-600 flex items-center justify-center flex-shrink-0">
                              <Check className="w-3 h-3 text-white" />
                            </div>
                            <div className="min-w-0">
                              <p className="text-xs text-white font-medium truncate">{acc.name}</p>
                              <p className="text-[10px] text-white/40">{acc.currency}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {hasConfig && (
                              <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-amber-500/30">
                                {t("accountSettings.ownKpi")}
                              </Badge>
                            )}
                            {isExpanded ? (
                              <ChevronUp className="w-3.5 h-3.5 text-white/60" />
                            ) : (
                              <ChevronDown className="w-3.5 h-3.5 text-white/60" />
                            )}
                          </div>
                        </button>

                        {/* Expanded per-account config */}
                        {isExpanded && (
                          <div className="px-3 py-3 bg-white/[0.03] space-y-3 border-t border-white/[0.06]">
                            {/* KPI fields — conditional on dashboard mode */}
                            <div className="grid grid-cols-2 gap-2">
                              {dashboardMode === "ecommerce" ? (
                                <>
                                  <Field className="space-y-1">
                                    <FieldLabel className="text-[10px] text-white/40 flex items-center gap-1">
                                      <TrendingUp className="w-2.5 h-2.5" /> ROAS
                                    </FieldLabel>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      placeholder={kpiTargetRoas || "domyślne"}
                                      value={cfg.kpiTargetRoas || ""}
                                      onChange={(e) =>
                                        updateAccountConfig(acc.id, "kpiTargetRoas", e.target.value)
                                      }
                                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                                    />
                                  </Field>
                                  <Field className="space-y-1">
                                    <FieldLabel className="text-[10px] text-white/40 flex items-center gap-1">
                                      <DollarSign className="w-2.5 h-2.5" /> CPA ({currLabel})
                                    </FieldLabel>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder="domyślne"
                                      value={cfg.kpiTargetCpa || ""}
                                      onChange={(e) =>
                                        updateAccountConfig(acc.id, "kpiTargetCpa", e.target.value)
                                      }
                                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                                    />
                                  </Field>
                                  <Field className="space-y-1">
                                    <FieldLabel className="text-[10px] text-white/40 flex items-center gap-1">
                                      <DollarSign className="w-2.5 h-2.5" /> CPM ({currLabel})
                                    </FieldLabel>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder="domyślne"
                                      value={cfg.kpiTargetCpm || ""}
                                      onChange={(e) =>
                                        updateAccountConfig(acc.id, "kpiTargetCpm", e.target.value)
                                      }
                                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                                    />
                                  </Field>
                                  <Field className="space-y-1">
                                    <FieldLabel className="text-[10px] text-white/40 flex items-center gap-1">
                                      <ShoppingCart className="w-2.5 h-2.5" /> AOV ({currLabel})
                                    </FieldLabel>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder="domyślne"
                                      value={cfg.kpiTargetAov || ""}
                                      onChange={(e) =>
                                        updateAccountConfig(acc.id, "kpiTargetAov", e.target.value)
                                      }
                                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                                    />
                                  </Field>
                                </>
                              ) : dashboardMode === "saas" ? (
                                <>
                                  <Field className="space-y-1">
                                    <FieldLabel className="text-[10px] text-white/40 flex items-center gap-1">
                                      <Users className="w-2.5 h-2.5" />{" "}
                                      {t("accountSettings.kpiTrial")}
                                    </FieldLabel>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder="domyślne"
                                      value={cfg.kpiCostPerTrial || ""}
                                      onChange={(e) =>
                                        updateAccountConfig(
                                          acc.id,
                                          "kpiCostPerTrial",
                                          e.target.value
                                        )
                                      }
                                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                                    />
                                  </Field>
                                  <Field className="space-y-1">
                                    <FieldLabel className="text-[10px] text-white/40 flex items-center gap-1">
                                      <DollarSign className="w-2.5 h-2.5" />{" "}
                                      {t("accountSettings.kpiSubscription")}
                                    </FieldLabel>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder="domyślne"
                                      value={cfg.kpiCostPerSubscription || ""}
                                      onChange={(e) =>
                                        updateAccountConfig(
                                          acc.id,
                                          "kpiCostPerSubscription",
                                          e.target.value
                                        )
                                      }
                                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                                    />
                                  </Field>
                                </>
                              ) : (
                                <>
                                  <Field className="space-y-1">
                                    <FieldLabel className="text-[10px] text-white/40 flex items-center gap-1">
                                      <Users className="w-2.5 h-2.5" />{" "}
                                      {t("accountSettings.kpiLead")}
                                    </FieldLabel>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder={kpiCostPerLead || "domyślne"}
                                      value={cfg.kpiCostPerLead || ""}
                                      onChange={(e) =>
                                        updateAccountConfig(
                                          acc.id,
                                          "kpiCostPerLead",
                                          e.target.value
                                        )
                                      }
                                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                                    />
                                  </Field>
                                  <Field className="space-y-1">
                                    <FieldLabel className="text-[10px] text-white/40 flex items-center gap-1">
                                      <Phone className="w-2.5 h-2.5" />{" "}
                                      {t("accountSettings.kpiCall")}
                                    </FieldLabel>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder={kpiCostPerCall || "domyślne"}
                                      value={cfg.kpiCostPerCall || ""}
                                      onChange={(e) =>
                                        updateAccountConfig(
                                          acc.id,
                                          "kpiCostPerCall",
                                          e.target.value
                                        )
                                      }
                                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                                    />
                                  </Field>
                                  <Field className="space-y-1">
                                    <FieldLabel className="text-[10px] text-white/40 flex items-center gap-1">
                                      <DollarSign className="w-2.5 h-2.5" />{" "}
                                      {t("accountSettings.kpiClient")}
                                    </FieldLabel>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      placeholder={kpiCostPerClient || "domyślne"}
                                      value={cfg.kpiCostPerClient || ""}
                                      onChange={(e) =>
                                        updateAccountConfig(
                                          acc.id,
                                          "kpiCostPerClient",
                                          e.target.value
                                        )
                                      }
                                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                                    />
                                  </Field>
                                  <Field className="space-y-1">
                                    <FieldLabel className="text-[10px] text-white/40 flex items-center gap-1">
                                      <TrendingUp className="w-2.5 h-2.5" /> ROAS
                                    </FieldLabel>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      placeholder={kpiTargetRoas || "domyślne"}
                                      value={cfg.kpiTargetRoas || ""}
                                      onChange={(e) =>
                                        updateAccountConfig(acc.id, "kpiTargetRoas", e.target.value)
                                      }
                                      className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white"
                                    />
                                  </Field>
                                </>
                              )}
                            </div>

                            {/* Conversion mapping */}
                            <div className="border-t border-white/[0.06] pt-2.5 space-y-2">
                              <p className="text-[10px] text-white/40 font-medium uppercase tracking-wider">
                                {t("accountSettings.conversionMapping")}
                              </p>
                              <div className="space-y-1.5">
                                {dashboardMode === "saas" ? (
                                  <>
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[10px] text-white/60 w-20 flex-shrink-0">
                                          Trial =
                                        </span>
                                        <Input
                                          placeholder="np. lead, offsite_conversion.fb_pixel_lead, complete_registration"
                                          value={cfg.conversionTrial || ""}
                                          onChange={(e) =>
                                            updateAccountConfig(
                                              acc.id,
                                              "conversionTrial",
                                              e.target.value
                                            )
                                          }
                                          className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white flex-1"
                                          list={`conv-${acc.id}-trial`}
                                        />
                                        <datalist id={`conv-${acc.id}-trial`}>
                                          {COMMON_CONVERSIONS.map((c) => (
                                            <option key={c} value={c} />
                                          ))}
                                        </datalist>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 px-2 text-[10px] text-white/50 hover:text-white"
                                          disabled={
                                            !cfg.conversionTrial ||
                                            verifyingConversion === `${acc.id}:conversionTrial`
                                          }
                                          onClick={() =>
                                            verifyConversion(
                                              acc.id,
                                              "conversionTrial",
                                              cfg.conversionTrial || ""
                                            )
                                          }
                                        >
                                          {verifyingConversion === `${acc.id}:conversionTrial` ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <Eye className="w-3 h-3" />
                                          )}
                                          <span className="ml-1">{t("accountSettings.check")}</span>
                                        </Button>
                                      </div>
                                      {conversionVerifyResult[`${acc.id}:conversionTrial`] && (
                                        <div
                                          className={`text-[10px] ml-[84px] px-2 py-1 rounded ${conversionVerifyResult[`${acc.id}:conversionTrial`]!.found ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}
                                        >
                                          {conversionVerifyResult[`${acc.id}:conversionTrial`]!
                                            .found
                                            ? t("accountSettings.conversionFound", {
                                                count:
                                                  conversionVerifyResult[
                                                    `${acc.id}:conversionTrial`
                                                  ]!.count,
                                              })
                                            : t("accountSettings.conversionNotFound", {
                                                type: cfg.conversionTrial,
                                              })}
                                          {conversionVerifyResult[`${acc.id}:conversionTrial`]!
                                            .allConversions.length > 0 && (
                                            <details className="mt-1">
                                              <summary className="cursor-pointer text-white/40 hover:text-white/60">
                                                {t("accountSettings.availableConversions", {
                                                  count:
                                                    conversionVerifyResult[
                                                      `${acc.id}:conversionTrial`
                                                    ]!.allConversions.length,
                                                })}
                                              </summary>
                                              <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                                                {conversionVerifyResult[
                                                  `${acc.id}:conversionTrial`
                                                ]!.allConversions.map((c) => (
                                                  <div
                                                    key={c.type}
                                                    className="flex justify-between text-white/50 cursor-pointer hover:text-white/80"
                                                    onClick={() => {
                                                      updateAccountConfig(
                                                        acc.id,
                                                        "conversionTrial",
                                                        c.type
                                                      );
                                                      setConversionVerifyResult((prev) => ({
                                                        ...prev,
                                                        [`${acc.id}:conversionTrial`]: null,
                                                      }));
                                                    }}
                                                  >
                                                    <span>{c.type}</span>
                                                    <span className="text-white/30">{c.count}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            </details>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[10px] text-white/60 w-20 flex-shrink-0">
                                          Subskrypcja =
                                        </span>
                                        <Input
                                          placeholder="np. purchase, offsite_conversion.fb_pixel_purchase"
                                          value={cfg.conversionSubscription || ""}
                                          onChange={(e) =>
                                            updateAccountConfig(
                                              acc.id,
                                              "conversionSubscription",
                                              e.target.value
                                            )
                                          }
                                          className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white flex-1"
                                          list={`conv-${acc.id}-sub`}
                                        />
                                        <datalist id={`conv-${acc.id}-sub`}>
                                          {COMMON_CONVERSIONS.map((c) => (
                                            <option key={c} value={c} />
                                          ))}
                                        </datalist>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 px-2 text-[10px] text-white/50 hover:text-white"
                                          disabled={
                                            !cfg.conversionSubscription ||
                                            verifyingConversion ===
                                              `${acc.id}:conversionSubscription`
                                          }
                                          onClick={() =>
                                            verifyConversion(
                                              acc.id,
                                              "conversionSubscription",
                                              cfg.conversionSubscription || ""
                                            )
                                          }
                                        >
                                          {verifyingConversion ===
                                          `${acc.id}:conversionSubscription` ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <Eye className="w-3 h-3" />
                                          )}
                                          <span className="ml-1">{t("accountSettings.check")}</span>
                                        </Button>
                                      </div>
                                      {conversionVerifyResult[
                                        `${acc.id}:conversionSubscription`
                                      ] && (
                                        <div
                                          className={`text-[10px] ml-[84px] px-2 py-1 rounded ${conversionVerifyResult[`${acc.id}:conversionSubscription`]!.found ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}
                                        >
                                          {conversionVerifyResult[
                                            `${acc.id}:conversionSubscription`
                                          ]!.found
                                            ? t("accountSettings.conversionFound", {
                                                count:
                                                  conversionVerifyResult[
                                                    `${acc.id}:conversionSubscription`
                                                  ]!.count,
                                              })
                                            : t("accountSettings.conversionNotFound", {
                                                type: cfg.conversionSubscription,
                                              })}
                                          {conversionVerifyResult[
                                            `${acc.id}:conversionSubscription`
                                          ]!.allConversions.length > 0 && (
                                            <details className="mt-1">
                                              <summary className="cursor-pointer text-white/40 hover:text-white/60">
                                                {t("accountSettings.availableConversions", {
                                                  count:
                                                    conversionVerifyResult[
                                                      `${acc.id}:conversionSubscription`
                                                    ]!.allConversions.length,
                                                })}
                                              </summary>
                                              <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                                                {conversionVerifyResult[
                                                  `${acc.id}:conversionSubscription`
                                                ]!.allConversions.map((c) => (
                                                  <div
                                                    key={c.type}
                                                    className="flex justify-between text-white/50 cursor-pointer hover:text-white/80"
                                                    onClick={() => {
                                                      updateAccountConfig(
                                                        acc.id,
                                                        "conversionSubscription",
                                                        c.type
                                                      );
                                                      setConversionVerifyResult((prev) => ({
                                                        ...prev,
                                                        [`${acc.id}:conversionSubscription`]: null,
                                                      }));
                                                    }}
                                                  >
                                                    <span>{c.type}</span>
                                                    <span className="text-white/30">{c.count}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            </details>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[10px] text-white/60 w-16 flex-shrink-0">
                                          Lead =
                                        </span>
                                        <Input
                                          placeholder="np. lead, offsite_conversion.fb_pixel_lead"
                                          value={cfg.conversionLead || ""}
                                          onChange={(e) =>
                                            updateAccountConfig(
                                              acc.id,
                                              "conversionLead",
                                              e.target.value
                                            )
                                          }
                                          className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white flex-1"
                                          list={`conv-${acc.id}-lead`}
                                        />
                                        <datalist id={`conv-${acc.id}-lead`}>
                                          {COMMON_CONVERSIONS.map((c) => (
                                            <option key={c} value={c} />
                                          ))}
                                        </datalist>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 px-2 text-[10px] text-white/50 hover:text-white"
                                          disabled={
                                            !cfg.conversionLead ||
                                            verifyingConversion === `${acc.id}:conversionLead`
                                          }
                                          onClick={() =>
                                            verifyConversion(
                                              acc.id,
                                              "conversionLead",
                                              cfg.conversionLead || ""
                                            )
                                          }
                                        >
                                          {verifyingConversion === `${acc.id}:conversionLead` ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <Eye className="w-3 h-3" />
                                          )}
                                          <span className="ml-1">{t("accountSettings.check")}</span>
                                        </Button>
                                      </div>
                                      {conversionVerifyResult[`${acc.id}:conversionLead`] && (
                                        <div
                                          className={`text-[10px] ml-[68px] px-2 py-1 rounded ${conversionVerifyResult[`${acc.id}:conversionLead`]!.found ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}
                                        >
                                          {conversionVerifyResult[`${acc.id}:conversionLead`]!.found
                                            ? t("accountSettings.conversionFound", {
                                                count:
                                                  conversionVerifyResult[
                                                    `${acc.id}:conversionLead`
                                                  ]!.count,
                                              })
                                            : t("accountSettings.conversionNotFound", {
                                                type: cfg.conversionLead,
                                              })}
                                          {conversionVerifyResult[`${acc.id}:conversionLead`]!
                                            .allConversions.length > 0 && (
                                            <details className="mt-1">
                                              <summary className="cursor-pointer text-white/40 hover:text-white/60">
                                                {t("accountSettings.availableConversions", {
                                                  count:
                                                    conversionVerifyResult[
                                                      `${acc.id}:conversionLead`
                                                    ]!.allConversions.length,
                                                })}
                                              </summary>
                                              <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                                                {conversionVerifyResult[
                                                  `${acc.id}:conversionLead`
                                                ]!.allConversions.map((c) => (
                                                  <div
                                                    key={c.type}
                                                    className="flex justify-between text-white/50 cursor-pointer hover:text-white/80"
                                                    onClick={() => {
                                                      updateAccountConfig(
                                                        acc.id,
                                                        "conversionLead",
                                                        c.type
                                                      );
                                                      setConversionVerifyResult((prev) => ({
                                                        ...prev,
                                                        [`${acc.id}:conversionLead`]: null,
                                                      }));
                                                    }}
                                                  >
                                                    <span>{c.type}</span>
                                                    <span className="text-white/30">{c.count}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            </details>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[10px] text-white/60 w-16 flex-shrink-0">
                                          Rozmowa =
                                        </span>
                                        <Input
                                          placeholder="np. schedule, onsite_conversion..."
                                          value={cfg.conversionCall || ""}
                                          onChange={(e) =>
                                            updateAccountConfig(
                                              acc.id,
                                              "conversionCall",
                                              e.target.value
                                            )
                                          }
                                          className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white flex-1"
                                          list={`conv-${acc.id}-call`}
                                        />
                                        <datalist id={`conv-${acc.id}-call`}>
                                          {COMMON_CONVERSIONS.map((c) => (
                                            <option key={c} value={c} />
                                          ))}
                                        </datalist>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 px-2 text-[10px] text-white/50 hover:text-white"
                                          disabled={
                                            !cfg.conversionCall ||
                                            verifyingConversion === `${acc.id}:conversionCall`
                                          }
                                          onClick={() =>
                                            verifyConversion(
                                              acc.id,
                                              "conversionCall",
                                              cfg.conversionCall || ""
                                            )
                                          }
                                        >
                                          {verifyingConversion === `${acc.id}:conversionCall` ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <Eye className="w-3 h-3" />
                                          )}
                                          <span className="ml-1">{t("accountSettings.check")}</span>
                                        </Button>
                                      </div>
                                      {conversionVerifyResult[`${acc.id}:conversionCall`] && (
                                        <div
                                          className={`text-[10px] ml-[68px] px-2 py-1 rounded ${conversionVerifyResult[`${acc.id}:conversionCall`]!.found ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}
                                        >
                                          {conversionVerifyResult[`${acc.id}:conversionCall`]!.found
                                            ? t("accountSettings.conversionFound", {
                                                count:
                                                  conversionVerifyResult[
                                                    `${acc.id}:conversionCall`
                                                  ]!.count,
                                              })
                                            : t("accountSettings.conversionNotFound", {
                                                type: cfg.conversionCall,
                                              })}
                                          {conversionVerifyResult[`${acc.id}:conversionCall`]!
                                            .allConversions.length > 0 && (
                                            <details className="mt-1">
                                              <summary className="cursor-pointer text-white/40 hover:text-white/60">
                                                {t("accountSettings.availableConversions", {
                                                  count:
                                                    conversionVerifyResult[
                                                      `${acc.id}:conversionCall`
                                                    ]!.allConversions.length,
                                                })}
                                              </summary>
                                              <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                                                {conversionVerifyResult[
                                                  `${acc.id}:conversionCall`
                                                ]!.allConversions.map((c) => (
                                                  <div
                                                    key={c.type}
                                                    className="flex justify-between text-white/50 cursor-pointer hover:text-white/80"
                                                    onClick={() => {
                                                      updateAccountConfig(
                                                        acc.id,
                                                        "conversionCall",
                                                        c.type
                                                      );
                                                      setConversionVerifyResult((prev) => ({
                                                        ...prev,
                                                        [`${acc.id}:conversionCall`]: null,
                                                      }));
                                                    }}
                                                  >
                                                    <span>{c.type}</span>
                                                    <span className="text-white/30">{c.count}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            </details>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[10px] text-white/60 w-16 flex-shrink-0">
                                          {t("accountSettings.saleLabel")}
                                        </span>
                                        <Input
                                          placeholder="np. purchase, offsite_conversion..."
                                          value={cfg.conversionSale || ""}
                                          onChange={(e) =>
                                            updateAccountConfig(
                                              acc.id,
                                              "conversionSale",
                                              e.target.value
                                            )
                                          }
                                          className="h-7 text-xs bg-[#030407] border-white/[0.1] text-white flex-1"
                                          list={`conv-${acc.id}-sale`}
                                        />
                                        <datalist id={`conv-${acc.id}-sale`}>
                                          {COMMON_CONVERSIONS.map((c) => (
                                            <option key={c} value={c} />
                                          ))}
                                        </datalist>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-7 px-2 text-[10px] text-white/50 hover:text-white"
                                          disabled={
                                            !cfg.conversionSale ||
                                            verifyingConversion === `${acc.id}:conversionSale`
                                          }
                                          onClick={() =>
                                            verifyConversion(
                                              acc.id,
                                              "conversionSale",
                                              cfg.conversionSale || ""
                                            )
                                          }
                                        >
                                          {verifyingConversion === `${acc.id}:conversionSale` ? (
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                          ) : (
                                            <Eye className="w-3 h-3" />
                                          )}
                                          <span className="ml-1">{t("accountSettings.check")}</span>
                                        </Button>
                                      </div>
                                      {conversionVerifyResult[`${acc.id}:conversionSale`] && (
                                        <div
                                          className={`text-[10px] ml-[68px] px-2 py-1 rounded ${conversionVerifyResult[`${acc.id}:conversionSale`]!.found ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}
                                        >
                                          {conversionVerifyResult[`${acc.id}:conversionSale`]!.found
                                            ? t("accountSettings.conversionFound", {
                                                count:
                                                  conversionVerifyResult[
                                                    `${acc.id}:conversionSale`
                                                  ]!.count,
                                              })
                                            : t("accountSettings.conversionNotFound", {
                                                type: cfg.conversionSale,
                                              })}
                                          {conversionVerifyResult[`${acc.id}:conversionSale`]!
                                            .allConversions.length > 0 && (
                                            <details className="mt-1">
                                              <summary className="cursor-pointer text-white/40 hover:text-white/60">
                                                {t("accountSettings.availableConversions", {
                                                  count:
                                                    conversionVerifyResult[
                                                      `${acc.id}:conversionSale`
                                                    ]!.allConversions.length,
                                                })}
                                              </summary>
                                              <div className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                                                {conversionVerifyResult[
                                                  `${acc.id}:conversionSale`
                                                ]!.allConversions.map((c) => (
                                                  <div
                                                    key={c.type}
                                                    className="flex justify-between text-white/50 cursor-pointer hover:text-white/80"
                                                    onClick={() => {
                                                      updateAccountConfig(
                                                        acc.id,
                                                        "conversionSale",
                                                        c.type
                                                      );
                                                      setConversionVerifyResult((prev) => ({
                                                        ...prev,
                                                        [`${acc.id}:conversionSale`]: null,
                                                      }));
                                                    }}
                                                  >
                                                    <span>{c.type}</span>
                                                    <span className="text-white/30">{c.count}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            </details>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ═══ Arkusz idealnego klienta — przeniesiony z osobnej zakładki sidebara ═══ */}
            <IdealClientSheetSection />

            {/* ═══ Integrations Section — compact summary, full management lives in Centrum Integracji ═══ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Plug className="w-4 h-4 text-purple-400" />
                <h3 className="text-sm font-semibold text-white">{t("integrations.title")}</h3>
              </div>
              {(() => {
                const items: { name: string; connected: boolean }[] = [
                  {
                    name: "Meta Ads",
                    connected: !!metaStatus?.connected && !metaStatus?.tokenExpired,
                  },
                  ...(canSeeGoogleAds
                    ? [{ name: "Google Ads", connected: !!googleAdsStatus?.connected }]
                    : []),
                  { name: "LeadConnector", connected: !!ghlStatus?.connected },
                  { name: "Allegro", connected: !!allegroStatus?.connected },
                  { name: "Shopify", connected: !!shopifyStatus?.connected },
                  { name: "BaseLinker", connected: !!baselinkerStatus?.connected },
                  { name: "Google Merchant", connected: !!gmcStatus?.connected },
                  { name: "TikTok", connected: !!tiktokStatus?.connected },
                ];
                const connectedCount = items.filter((i) => i.connected).length;
                return (
                  <div className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-white/60">
                        {t("integrations.summary", "Połączone")}:{" "}
                        <span className="text-white font-semibold">
                          {connectedCount}/{items.length}
                        </span>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          onOpenChange(false);
                          window.location.href = "/integracje";
                        }}
                        className="text-[11px] font-medium text-[#ef3e29] hover:text-[#d63520] transition-colors"
                      >
                        {t("integrations.openHub", "Centrum Integracji")} →
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {items.map((it) => (
                        <span
                          key={it.name}
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-medium border ${it.connected ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-white/[0.08] bg-white/[0.02] text-white/40"}`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${it.connected ? "bg-emerald-400" : "bg-white/30"}`}
                          />
                          {it.name}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* ═══ Section 3: Data Sources ═══ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <h3 className="text-sm font-semibold text-white">{t("dataSources.title")}</h3>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center">
                      <svg
                        viewBox="0 0 24 24"
                        className="w-3.5 h-3.5 text-white"
                        fill="currentColor"
                      >
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </div>
                    <span className="text-sm text-white">Meta Ads</span>
                  </div>
                  <Switch
                    checked={dataSources.meta}
                    onCheckedChange={(checked) =>
                      setDataSources((prev) => ({ ...prev, meta: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-orange-600 flex items-center justify-center">
                      <Phone className="w-3.5 h-3.5 text-white" />
                    </div>
                    <span className="text-sm text-white">LeadConnector CRM</span>
                  </div>
                  <Switch
                    checked={dataSources.ghl}
                    onCheckedChange={(checked) =>
                      setDataSources((prev) => ({ ...prev, ghl: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-emerald-600 flex items-center justify-center">
                      <FileSpreadsheet className="w-3.5 h-3.5 text-white" />
                    </div>
                    <div>
                      <span className="text-sm text-white">Google Sheets</span>
                      <p className="text-[10px] text-white/40">
                        {t("dataSources.googleSheetsAlt")}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={dataSources.googleSheets}
                    onCheckedChange={(checked) =>
                      setDataSources((prev) => ({ ...prev, googleSheets: checked }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded bg-[#ff7a59] flex items-center justify-center">
                      <svg
                        viewBox="0 0 24 24"
                        className="w-3.5 h-3.5 text-white"
                        fill="currentColor"
                      >
                        <path d="M18.16 5.67V3.39c.61-.29 1.04-.9 1.04-1.61A1.78 1.78 0 0017.42 0a1.78 1.78 0 00-1.78 1.78c0 .71.42 1.32 1.04 1.61v2.28a4.87 4.87 0 00-2.33 1.17l-6.15-4.78c.05-.18.08-.37.08-.57A2.49 2.49 0 005.79 0 2.49 2.49 0 003.3 2.49c0 1.38 1.12 2.49 2.49 2.49.49 0 .94-.14 1.33-.39l6.04 4.7a4.88 4.88 0 00-.62 2.39c0 .89.24 1.72.65 2.44l-1.93 1.93a1.62 1.62 0 00-.48-.08 1.64 1.64 0 00-1.64 1.64c0 .9.73 1.64 1.64 1.64.9 0 1.64-.73 1.64-1.64 0-.17-.03-.33-.08-.48l1.9-1.9a4.88 4.88 0 008.18-3.55 4.88 4.88 0 00-4.06-4.81zm-1.3 7.92a2.77 2.77 0 110-5.54 2.77 2.77 0 010 5.54z" />
                      </svg>
                    </div>
                    <div>
                      <span className="text-sm text-white">HubSpot CRM</span>
                      <p className="text-[10px] text-white/40">
                        {hubspotStatus?.connected
                          ? `✓ ${hubspotStatus.portalName || t("dataSources.hubspotConnectedSimple")}`
                          : t("dataSources.hubspotNotConnected")}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={dataSources.hubspot}
                    onCheckedChange={(checked) =>
                      setDataSources((prev) => ({ ...prev, hubspot: checked }))
                    }
                  />
                </div>
              </div>
            </div>

            {/* Google Sheets removed — connect/configure inside Centrum Integracji. */}

            {/* ═══ Section 4: Global KPI Defaults ═══ */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-amber-400" />
                <h3 className="text-sm font-semibold text-white">{t("kpiDefaults.title")}</h3>
              </div>
              <p className="text-xs text-white/60">{t("kpiDefaults.desc")}</p>

              <div className="grid grid-cols-2 gap-dense">
                {dashboardMode === "ecommerce" ? (
                  <>
                    <Field className="space-y-1.5">
                      <FieldLabel className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> {t("kpiDefaults.targetRoas")}
                      </FieldLabel>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="np. 3.0"
                        value={kpiTargetRoas}
                        onChange={(e) => setKpiTargetRoas(e.target.value)}
                        className="h-9 bg-[#0a0c10] border-white/[0.1] text-white text-sm"
                      />
                    </Field>
                    <Field className="space-y-1.5">
                      <FieldLabel className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />{" "}
                        {t("kpiDefaults.targetCpa", { currency: currLabel })}
                      </FieldLabel>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="np. 50.00"
                        value={kpiCostPerClient}
                        onChange={(e) => setKpiCostPerClient(e.target.value)}
                        className="h-9 bg-[#0a0c10] border-white/[0.1] text-white text-sm"
                      />
                    </Field>
                    <Field className="space-y-1.5">
                      <FieldLabel className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />{" "}
                        {t("kpiDefaults.targetCpm", { currency: currLabel })}
                      </FieldLabel>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="np. 15.00"
                        value={kpiCostPerCall}
                        onChange={(e) => setKpiCostPerCall(e.target.value)}
                        className="h-9 bg-[#0a0c10] border-white/[0.1] text-white text-sm"
                      />
                    </Field>
                    <Field className="space-y-1.5">
                      <FieldLabel className="flex items-center gap-1">
                        <ShoppingCart className="w-3 h-3" />{" "}
                        {t("kpiDefaults.targetAov", { currency: currLabel })}
                      </FieldLabel>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="np. 150.00"
                        value={kpiCostPerLead}
                        onChange={(e) => setKpiCostPerLead(e.target.value)}
                        className="h-9 bg-[#0a0c10] border-white/[0.1] text-white text-sm"
                      />
                    </Field>
                  </>
                ) : (
                  <>
                    <Field className="space-y-1.5">
                      <FieldLabel className="flex items-center gap-1">
                        <Users className="w-3 h-3" />{" "}
                        {t("kpiDefaults.costPerLead", { currency: currLabel })}
                      </FieldLabel>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="np. 25.00"
                        value={kpiCostPerLead}
                        onChange={(e) => setKpiCostPerLead(e.target.value)}
                        className="h-9 bg-[#0a0c10] border-white/[0.1] text-white text-sm"
                      />
                    </Field>
                    <Field className="space-y-1.5">
                      <FieldLabel className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />{" "}
                        {t("kpiDefaults.costPerCall", { currency: currLabel })}
                      </FieldLabel>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="np. 80.00"
                        value={kpiCostPerCall}
                        onChange={(e) => setKpiCostPerCall(e.target.value)}
                        className="h-9 bg-[#0a0c10] border-white/[0.1] text-white text-sm"
                      />
                    </Field>
                    <Field className="space-y-1.5">
                      <FieldLabel className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />{" "}
                        {t("kpiDefaults.costPerClient", { currency: currLabel })}
                      </FieldLabel>
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="np. 200.00"
                        value={kpiCostPerClient}
                        onChange={(e) => setKpiCostPerClient(e.target.value)}
                        className="h-9 bg-[#0a0c10] border-white/[0.1] text-white text-sm"
                      />
                    </Field>
                    <Field className="space-y-1.5">
                      <FieldLabel className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> {t("kpiDefaults.targetRoas")}
                      </FieldLabel>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="np. 3.0"
                        value={kpiTargetRoas}
                        onChange={(e) => setKpiTargetRoas(e.target.value)}
                        className="h-9 bg-[#0a0c10] border-white/[0.1] text-white text-sm"
                      />
                    </Field>
                  </>
                )}
              </div>
            </div>

            {/* ═══ Section 4b: KPI Targets (quarterly + monthly) ═══ */}
            <QuarterlyTargetsSection
              quarterlyTargets={quarterlyTargets}
              onUpdate={setQuarterlyTargets}
              monthlyTargets={monthlyTargets}
              onUpdateMonthly={setMonthlyTargets}
              dashboardMode={dashboardMode}
            />

            {/* Allegro Catalog Monitor — single-line toggle. Slack/Allegro
                connection management lives in Centrum Integracji. */}
            <div className="flex items-center justify-between bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-orange-600 flex items-center justify-center">
                  <Eye className="w-3.5 h-3.5 text-white" />
                </div>
                <div>
                  <span className="text-sm text-white">{t("allegroMonitor.toggleLabel")}</span>
                  <p className="text-[10px] text-white/40">
                    {t(
                      "allegroMonitor.requirementHint",
                      "Wymaga połączenia Slack + Allegro w Centrum Integracji"
                    )}
                  </p>
                </div>
              </div>
              <Switch checked={allegroMonitorEnabled} onCheckedChange={setAllegroMonitorEnabled} />
            </div>
          </div>
        )}

        {/* KPI Monitoring toggle */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider">
            {t("kpiMonitor.sectionTitle")}
          </h4>
          <div className="flex items-center justify-between bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-amber-600 flex items-center justify-center">
                <Activity className="w-3.5 h-3.5 text-white" />
              </div>
              <div>
                <span className="text-sm text-white">{t("kpiMonitor.toggleLabel")}</span>
                <p className="text-[10px] text-white/40">{t("kpiMonitor.toggleDesc")}</p>
              </div>
            </div>
            <Switch checked={monitoringEnabled} onCheckedChange={setMonitoringEnabled} />
          </div>
          {monitoringEnabled && (
            <div className="bg-white/[0.03] rounded-lg px-3 py-2 border border-white/[0.06]">
              <p className="text-[10px] text-white/40 mb-1">{t("kpiMonitor.whatTitle")}</p>
              <ul className="text-[10px] text-white/50 space-y-0.5 list-disc pl-3">
                <li>{t("kpiMonitor.item1")}</li>
                <li>{t("kpiMonitor.item2")}</li>
                <li>{t("kpiMonitor.item3")}</li>
                <li>{t("kpiMonitor.item4")}</li>
              </ul>
              <p className="text-[10px] text-white/30 mt-1">{t("kpiMonitor.bellNote")}</p>
            </div>
          )}
        </div>

        {/* Integrations Hub link */}
        <button
          onClick={() => {
            onOpenChange(false);
            window.location.href = "/integracje";
          }}
          className="w-full flex items-center gap-dense p-3 rounded-xl bg-gradient-to-r from-red-500/10 to-transparent border border-red-500/20 hover:border-red-500/40 transition-all group"
        >
          <div className="p-2 rounded-lg bg-red-500/15 text-red-400 group-hover:bg-red-500/25 transition-colors">
            <Layers className="w-4 h-4" />
          </div>
          <div className="flex-1 text-left">
            <div className="text-sm font-medium text-white">{t("integrationHub.title")}</div>
            <div className="text-xs text-white/50">{t("integrationHub.desc")}</div>
          </div>
          <ExternalLink className="w-4 h-4 text-white/30 group-hover:text-white/60" />
        </button>

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-2 border-t border-white/[0.08]">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onOpenChange(false)}
            className="text-white/60 hover:text-white"
          >
            {t("cancel")}
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {saveMutation.isPending ? (
              <>
                <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("saving")}
              </>
            ) : (
              t("save")
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Helper: get current quarter key ───
function getCurrentQuarterKey(): string {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-Q${q}`;
}

function getNextQuarterKey(): string {
  const now = new Date();
  const q = Math.ceil((now.getMonth() + 1) / 3);
  if (q === 4) return `${now.getFullYear() + 1}-Q1`;
  return `${now.getFullYear()}-Q${q + 1}`;
}

// ─── Period Targets Fields — renders the right inputs per business type ───
function PeriodTargetFields({
  target,
  dashboardMode,
  onChange,
}: {
  target: QuarterlyTarget;
  dashboardMode?: string;
  onChange: (field: keyof QuarterlyTarget, value: string) => void;
}) {
  const { t } = useTranslation("analyticsSettings");
  const dc = useDashboardCurrency();
  const currSymbol = dc.symbol;
  const INPUT_CLS = "h-8 bg-[#0a0c10] border-white/[0.1] text-white text-xs";
  const LABEL_CLS = "text-[10px] text-white/50 flex items-center gap-1";

  if (dashboardMode === "ecommerce") {
    return (
      <div className="grid grid-cols-2 gap-2">
        <Field className="space-y-1">
          <FieldLabel className={LABEL_CLS}>
            <DollarSign className="w-2.5 h-2.5" />{" "}
            {t("periodFields.purchaseValue", { currency: currSymbol })}
          </FieldLabel>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder="np. 50000"
            value={target.targetPurchaseValue ?? ""}
            onChange={(e) => onChange("targetPurchaseValue", e.target.value)}
            className={INPUT_CLS}
          />
        </Field>
        <Field className="space-y-1">
          <FieldLabel className={LABEL_CLS}>
            <TrendingUp className="w-2.5 h-2.5" /> {t("periodFields.roas")}
          </FieldLabel>
          <Input
            type="number"
            min="0"
            step="0.1"
            placeholder="np. 4.0"
            value={target.targetRoas ?? ""}
            onChange={(e) => onChange("targetRoas", e.target.value)}
            className={INPUT_CLS}
          />
        </Field>
        <Field className="space-y-1">
          <FieldLabel className={LABEL_CLS}>
            <ShoppingCart className="w-2.5 h-2.5" /> {t("periodFields.purchases")}
          </FieldLabel>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder="np. 200"
            value={target.targetPurchases ?? ""}
            onChange={(e) => onChange("targetPurchases", e.target.value)}
            className={INPUT_CLS}
          />
        </Field>
        <Field className="space-y-1">
          <FieldLabel className={LABEL_CLS}>
            <DollarSign className="w-2.5 h-2.5" />{" "}
            {t("periodFields.costPerPurchase", { currency: currSymbol })}
          </FieldLabel>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder="np. 80"
            value={target.targetCostPerPurchase ?? ""}
            onChange={(e) => onChange("targetCostPerPurchase", e.target.value)}
            className={INPUT_CLS}
          />
        </Field>
      </div>
    );
  }

  if (dashboardMode === "saas") {
    return (
      <div className="grid grid-cols-2 gap-2">
        <Field className="space-y-1">
          <FieldLabel className={LABEL_CLS}>
            <Users className="w-2.5 h-2.5" /> {t("periodFields.trials")}
          </FieldLabel>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder="np. 300"
            value={target.targetTrials ?? ""}
            onChange={(e) => onChange("targetTrials", e.target.value)}
            className={INPUT_CLS}
          />
        </Field>
        <Field className="space-y-1">
          <FieldLabel className={LABEL_CLS}>
            <DollarSign className="w-2.5 h-2.5" /> {t("periodFields.paidAccounts")}
          </FieldLabel>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder="np. 50"
            value={target.targetPaid ?? ""}
            onChange={(e) => onChange("targetPaid", e.target.value)}
            className={INPUT_CLS}
          />
        </Field>
        <Field className="space-y-1">
          <FieldLabel className={LABEL_CLS}>
            <DollarSign className="w-2.5 h-2.5" /> {t("periodFields.mrr", { currency: currSymbol })}
          </FieldLabel>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder="np. 20000"
            value={target.targetMrr ?? ""}
            onChange={(e) => onChange("targetMrr", e.target.value)}
            className={INPUT_CLS}
          />
        </Field>
        <Field className="space-y-1">
          <FieldLabel className={LABEL_CLS}>
            <DollarSign className="w-2.5 h-2.5" />{" "}
            {t("periodFields.cpaRegistration", { currency: currSymbol })}
          </FieldLabel>
          <Input
            type="number"
            min="0"
            step="1"
            placeholder="np. 30"
            value={target.targetCpa ?? ""}
            onChange={(e) => onChange("targetCpa", e.target.value)}
            className={INPUT_CLS}
          />
        </Field>
      </div>
    );
  }

  // lead_gen_basic | lead_gen_advanced | vsl_funnel
  const isAdvanced = dashboardMode === "lead_gen_advanced";
  return (
    <div className="grid grid-cols-3 gap-2">
      <Field className="space-y-1">
        <FieldLabel className={LABEL_CLS}>
          <Users className="w-2.5 h-2.5" /> {t("periodFields.leads")}
        </FieldLabel>
        <Input
          type="number"
          min="0"
          step="1"
          placeholder="np. 300"
          value={target.targetLeads ?? ""}
          onChange={(e) => onChange("targetLeads", e.target.value)}
          className={INPUT_CLS}
        />
      </Field>
      <Field className="space-y-1">
        <FieldLabel className={LABEL_CLS}>
          <DollarSign className="w-2.5 h-2.5" /> {t("periodFields.cpl", { currency: currSymbol })}
        </FieldLabel>
        <Input
          type="number"
          min="0"
          step="1"
          placeholder="np. 50"
          value={target.targetCpl ?? ""}
          onChange={(e) => onChange("targetCpl", e.target.value)}
          className={INPUT_CLS}
        />
      </Field>
      {isAdvanced ? (
        <>
          <Field className="space-y-1">
            <FieldLabel className={LABEL_CLS}>
              <Phone className="w-2.5 h-2.5" /> {t("periodFields.calls")}
            </FieldLabel>
            <Input
              type="number"
              min="0"
              step="1"
              placeholder="np. 50"
              value={target.targetCalls ?? ""}
              onChange={(e) => onChange("targetCalls", e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field className="space-y-1">
            <FieldLabel className={LABEL_CLS}>
              <DollarSign className="w-2.5 h-2.5" />{" "}
              {t("periodFields.cpbc", { currency: currSymbol })}
            </FieldLabel>
            <Input
              type="number"
              min="0"
              step="1"
              placeholder="np. 120"
              value={target.targetCpbc ?? ""}
              onChange={(e) => onChange("targetCpbc", e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field className="space-y-1">
            <FieldLabel className={LABEL_CLS}>
              <DollarSign className="w-2.5 h-2.5" /> {t("periodFields.sales")}
            </FieldLabel>
            <Input
              type="number"
              min="0"
              step="1"
              placeholder="np. 20"
              value={target.targetSales ?? ""}
              onChange={(e) => onChange("targetSales", e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field className="space-y-1">
            <FieldLabel className={LABEL_CLS}>
              <TrendingUp className="w-2.5 h-2.5" /> {t("periodFields.roas")}
            </FieldLabel>
            <Input
              type="number"
              min="0"
              step="0.1"
              placeholder="np. 3.0"
              value={target.targetRoas ?? ""}
              onChange={(e) => onChange("targetRoas", e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field className="space-y-1">
            <FieldLabel className={LABEL_CLS}>
              <DollarSign className="w-2.5 h-2.5" />{" "}
              {t("periodFields.purchaseValue", { currency: currSymbol })}
            </FieldLabel>
            <Input
              type="number"
              min="0"
              step="1"
              placeholder="np. 50000"
              value={target.targetPurchaseValue ?? ""}
              onChange={(e) => onChange("targetPurchaseValue", e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
        </>
      ) : (
        <>
          <Field className="space-y-1">
            <FieldLabel className={LABEL_CLS}>
              <DollarSign className="w-2.5 h-2.5" /> {t("periodFields.sales")}
            </FieldLabel>
            <Input
              type="number"
              min="0"
              step="1"
              placeholder="np. 20"
              value={target.targetSales ?? ""}
              onChange={(e) => onChange("targetSales", e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field className="space-y-1">
            <FieldLabel className={LABEL_CLS}>
              <TrendingUp className="w-2.5 h-2.5" /> {t("periodFields.roas")}
            </FieldLabel>
            <Input
              type="number"
              min="0"
              step="0.1"
              placeholder="np. 3.0"
              value={target.targetRoas ?? ""}
              onChange={(e) => onChange("targetRoas", e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
          <Field className="space-y-1">
            <FieldLabel className={LABEL_CLS}>
              <DollarSign className="w-2.5 h-2.5" />{" "}
              {t("periodFields.purchaseValue", { currency: currSymbol })}
            </FieldLabel>
            <Input
              type="number"
              min="0"
              step="1"
              placeholder="np. 50000"
              value={target.targetPurchaseValue ?? ""}
              onChange={(e) => onChange("targetPurchaseValue", e.target.value)}
              className={INPUT_CLS}
            />
          </Field>
        </>
      )}
    </div>
  );
}

// ─── Quarterly + Monthly Targets Sub-Component ───
function QuarterlyTargetsSection({
  quarterlyTargets,
  onUpdate,
  monthlyTargets,
  onUpdateMonthly,
  dashboardMode,
}: {
  quarterlyTargets: QuarterlyTargets;
  onUpdate: (targets: QuarterlyTargets) => void;
  monthlyTargets: MonthlyTargets;
  onUpdateMonthly: (targets: MonthlyTargets) => void;
  dashboardMode?: string;
}) {
  const { t } = useTranslation("analyticsSettings");
  const [tab, setTab] = useState<"quarterly" | "monthly">("quarterly");

  const currentQ = getCurrentQuarterKey();
  const nextQ = getNextQuarterKey();
  const quarters = [currentQ, nextQ];

  // Build current + next 2 months as keys like "2026-03"
  const now = new Date();
  const monthKeys: string[] = [];
  for (let i = 0; i < 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    monthKeys.push(key);
  }

  const monthNames = t("kpiTargets.monthNames", { returnObjects: true }) as string[];

  const formatMonthLabel = (key: string): string => {
    const [year, month] = key.split("-");
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  const formatQuarterLabel = (qKey: string): string => {
    const [year, q] = qKey.split("-");
    return `${q} ${year}`;
  };

  const updateQuarter = (qKey: string, field: keyof QuarterlyTarget, value: string) => {
    const prev = quarterlyTargets[qKey] ?? {};
    onUpdate({ ...quarterlyTargets, [qKey]: { ...prev, [field]: value ? Number(value) : null } });
  };

  const updateMonthly = (mKey: string, field: keyof QuarterlyTarget, value: string) => {
    const prev = monthlyTargets[mKey] ?? {};
    onUpdateMonthly({
      ...monthlyTargets,
      [mKey]: { ...prev, [field]: value ? Number(value) : null },
    });
  };

  const typeLabel =
    dashboardMode === "ecommerce"
      ? t("kpiTargets.typeLabelEcommerce")
      : dashboardMode === "saas"
        ? t("kpiTargets.typeLabelSaas")
        : dashboardMode === "lead_gen_advanced"
          ? t("kpiTargets.typeLabelAdvanced")
          : t("kpiTargets.typeLabelBasic");

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-blue-400" />
        <h3 className="text-sm font-semibold text-white">{t("kpiTargets.title")}</h3>
      </div>
      <p className="text-xs text-white/60">{t("kpiTargets.desc", { typeLabel })}</p>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-white/[0.04] rounded-md p-0.5 w-fit">
        {(["quarterly", "monthly"] as const).map((tabKey) => (
          <button
            key={tabKey}
            onClick={() => setTab(tabKey)}
            className={`text-[11px] px-3 py-1 rounded transition-colors ${
              tab === tabKey ? "bg-white/[0.1] text-white" : "text-white/40 hover:text-white/70"
            }`}
          >
            {tabKey === "quarterly" ? t("kpiTargets.quarterly") : t("kpiTargets.monthly")}
          </button>
        ))}
      </div>

      {tab === "quarterly" &&
        quarters.map((qKey) => {
          const target = quarterlyTargets[qKey] ?? {};
          const isCurrent = qKey === currentQ;
          return (
            <div
              key={qKey}
              className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-3 space-y-2"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-white">{formatQuarterLabel(qKey)}</span>
                {isCurrent && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-600/30 text-blue-300">
                    {t("kpiTargets.current")}
                  </span>
                )}
              </div>
              <PeriodTargetFields
                target={target}
                dashboardMode={dashboardMode}
                onChange={(f, v) => updateQuarter(qKey, f, v)}
              />
            </div>
          );
        })}

      {tab === "monthly" &&
        monthKeys.map((mKey, idx) => {
          const target = monthlyTargets[mKey] ?? {};
          const isCurrent = idx === 0;
          return (
            <div
              key={mKey}
              className="bg-white/[0.04] border border-white/[0.08] rounded-lg p-3 space-y-2"
            >
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-white">{formatMonthLabel(mKey)}</span>
                {isCurrent && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-600/30 text-blue-300">
                    {t("kpiTargets.current")}
                  </span>
                )}
              </div>
              <PeriodTargetFields
                target={target}
                dashboardMode={dashboardMode}
                onChange={(f, v) => updateMonthly(mKey, f, v)}
              />
            </div>
          );
        })}
    </div>
  );
}
